import { BarChart } from '@mantine/charts'
import {
	Alert,
	Badge,
	Box,
	Button,
	Card,
	Divider,
	Flex,
	Grid,
	Group,
	Image,
	ScrollArea,
	Skeleton,
	Stack,
	Table,
	Tabs,
	Text,
	Title,
} from '@mantine/core'
import { DateInput } from '@mantine/dates'
import { IconCalendar, IconInfoCircle } from '@tabler/icons-react'
import { Fragment, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import placeholder from '../../../assets/Placholder-01.png'
import userPlaceholder from '../../../assets/user.png'
import { StatusBox } from '../../../components/StatusBox.tsx'
import { VerticalBar } from '../../../components/VerticalProgress.tsx'
import Colors from '../../../constants/colors.ts'
import { useResponsiveHooks } from '../../../hooks/useResponsiveHook.tsx'
import { getAllBusinessesNamesAction } from '../../../store/business/businessesActions.ts'
import {
	getBusinessProductCountAction,
	getBusinessStatsAction,
	getBusinessTransactionsAction,
	getOrdersByBusinessIdAction,
	getTopBuyersDataAction,
	getTopRatedProductsDataAction,
} from '../../../store/seller/sellerActions.ts'
import { useAppDispatch, useAppSelector } from '../../../store/store.ts'
import { getUser } from '../../../utils/storage.ts'

export interface IAllBusinessesNames {
	success: boolean
	businesses: Business[]
}

export interface Business {
	_id: string
	title: string
	image: string
}

export interface IBusinessStatsResponse {
	success: boolean
	data: Data
}

export interface Data {
	totalRevenue: number
}

const data = [
	{ month: 'January', Smartphones: 1200, Laptops: 900, Tablets: 200 },
	{ month: 'February', Smartphones: 1900, Laptops: 1200, Tablets: 400 },
	{ month: 'March', Smartphones: 400, Laptops: 1000, Tablets: 200 },
	{ month: 'April', Smartphones: 1000, Laptops: 200, Tablets: 800 },
	{ month: 'May', Smartphones: 800, Laptops: 1400, Tablets: 1200 },
	{ month: 'June', Smartphones: 750, Laptops: 600, Tablets: 1000 },
]

const Dashboard = () => {
	const { dashboardStats } = useAppSelector((state) => state.business)
	const dispatch = useAppDispatch()
	const user = getUser()
	const navigate = useNavigate()
	const { authUser } = useAppSelector((state) => state.account)
	const {
		topBuyers,
		topRatedProducts,
		BusinessTransactions,
		ordersByBusiness,
		businessProductCount,
		loader,
		businessStats,
	} = useAppSelector((state) => state.seller)
	const { isLaptop, isMobile } = useResponsiveHooks()
	const [activeTab, setActiveTab] = useState<string | null>('all')

	const primaryBusiness = useMemo(
		() => authUser?.user?.primaryBusiness?._id || user?.businessID?._id,
		[user?.businessID, authUser?.user?.primaryBusiness]
	)

	useEffect(() => {
		dispatch(getAllBusinessesNamesAction())
		dispatch(
			getBusinessProductCountAction({
				id: primaryBusiness || '',
				status: activeTab || 'all',
			})
		)
	}, [activeTab, dispatch, primaryBusiness]) // ✅ ADD primaryBusiness

	useEffect(() => {
		if (!primaryBusiness) return

		dispatch(getTopBuyersDataAction(primaryBusiness))
		dispatch(getTopRatedProductsDataAction(primaryBusiness))
		dispatch(getBusinessTransactionsAction(primaryBusiness))
		dispatch(getBusinessStatsAction(primaryBusiness))

		dispatch(
			getOrdersByBusinessIdAction({
				businessId: primaryBusiness,
				status: '',
			})
		)
	}, [dispatch, primaryBusiness]) // ✅ ADD primaryBusiness HERE

	const rows =
		BusinessTransactions?.transactions?.length > 0 ? (
			BusinessTransactions.transactions.map((element, index) => (
				<Table.Tr key={index}>
					<Table.Td>
						{loader.gettingBusinessTransactions ? (
							<Skeleton h={30} w={130} />
						) : (
							element.title
						)}
					</Table.Td>
					<Table.Td>
						{loader.gettingBusinessTransactions ? (
							<Skeleton h={30} w={80} />
						) : (
							<Text>$ {element?.price?.amount.toFixed(2)}</Text>
						)}
					</Table.Td>
					<Table.Td>
						{loader.gettingBusinessTransactions ? (
							<Skeleton h={30} w={80} />
						) : (
							<Text>{new Date(element?.updatedAt).toLocaleDateString()}</Text>
						)}
					</Table.Td>
					<Table.Td>
						{loader.gettingBusinessTransactions ? (
							<Skeleton h={30} w={80} />
						) : (
							<Badge
								size='md'
								variant='light'
								color={element?.type === 'paid' ? 'green' : 'red'}
							>
								{element?.type}
							</Badge>
						)}
					</Table.Td>
				</Table.Tr>
			))
		) : !loader.gettingBusinessTransactions ? (
			<Table.Tr>
				<Table.Td colSpan={4}>
					<Flex
						w='100%'
						py='md'
						justify={'center'}
						align={'center'}
						style={{ textAlign: 'center' }}
					>
						<Text c='black' size='lg' fw={500}>
							No Transactions found
						</Text>
					</Flex>
				</Table.Td>
			</Table.Tr>
		) : null

	return (
		<Flex direction='column' gap='lg' mb='xs' m='md'>
			{dashboardStats?.stats?.warning?.type === 'warning' && (
				<Alert
					style={{ cursor: 'pointer' }}
					onClick={() => navigate('/seller/new-business')}
					variant='light'
					color='red'
					title={dashboardStats?.stats?.warning?.message}
					icon={<IconInfoCircle />}
				></Alert>
			)}
			{/* <LoadingOverlay visible={loaders.gettingDashboardStats} /> */}
			<Flex
				direction={isMobile ? 'column' : 'column'}
				w={'100%'}
				justify={'space-between'}
				align={isMobile ? 'start' : 'start'}
				gap={10}
			>
				<Title order={2}>Dashboard</Title>
				<Title order={4} fw={'normal'}>
					Good Morning,{' '}
					<span
						style={{
							color: Colors.primaryColor,
						}}
					>
						{user?.firstName}
					</span>
				</Title>
			</Flex>

			<Grid>
				<Grid.Col span={{ base: 12, md: 12, lg: 8 }}>
					<Grid grow>
						<Grid.Col span={{ base: 12, md: 12, lg: 6 }}>
							<Card
								withBorder
								style={{
									borderColor: '#E5E7EB',
								}}
								radius={'md'}
							>
								<Title
									order={isMobile ? 4 : 2}
									fw={isMobile ? 'bold' : 'normal'}
									mb={10}
								>
									Store Earnings
								</Title>
								<Flex w='100%' align={'center'} justify={'space-between'}>
									<Flex
										direction={'column'}
										gap={20}
										w={isMobile ? '72%' : '64%'}
									>
										<Flex gap={10}>
											<DateInput
												label='From'
												size='md'
												placeholder='Choose'
												rightSection={<IconCalendar />}
												color={Colors.secondaryColor}
												radius={'md'}
												styles={{
													label: {
														color: Colors.secondaryColor,
													},
												}}
											/>
											<DateInput
												label='To'
												size='md'
												placeholder='Choose'
												rightSection={<IconCalendar />}
												radius={'md'}
												styles={{
													label: {
														color: Colors.secondaryColor,
													},
												}}
											/>
										</Flex>
										<Flex>
											<Box
												w={'50%'}
												style={{
													borderLeft: `3px solid ${Colors.primaryColor}`,
													paddingLeft: 10,
												}}
											>
												<Text size={isMobile ? 'sm' : 'xs'}>
													Total Earnings
												</Text>
												<Text size={isMobile ? 'md' : 'md'} fw={'bold'}>
													{loader.gettingBusinessStats ? (
														<Skeleton h={25} w={80} />
													) : (
														businessStats?.data?.totalRevenue.toFixed(2)
													)}
												</Text>
											</Box>
											<Box
												style={{
													borderLeft: `3px solid ${Colors.lightGreen}`,
													paddingLeft: 10,
												}}
											>
												<Text size={isMobile ? 'sm' : 'xs'}>Net Profit</Text>
												<Text size={isMobile ? 'md' : 'md'} fw={'bold'}>
													{loader.gettingBusinessStats ? (
														<Skeleton h={25} w={80} />
													) : (
														businessStats?.data?.totalRevenue.toFixed(2)
													)}
												</Text>
											</Box>
										</Flex>
									</Flex>
									<Flex w={isMobile ? '25%' : '40%'} justify={'end'}>
										<Group align='end' gap={isMobile ? 'sm' : 'lg'}>
											<VerticalBar
												value={isMobile ? 85 : 120}
												color='#A8F64D'
												width={isMobile ? 30 : 55}
											/>
											<VerticalBar
												value={isMobile ? 130 : 180}
												color='#4CAF50'
												width={isMobile ? 30 : 55}
											/>
										</Group>
									</Flex>
								</Flex>
							</Card>
						</Grid.Col>
						<Grid.Col span={{ base: 12, md: 12, lg: 6 }}>
							<Card
								withBorder
								style={{
									borderColor: '#E5E7EB',
								}}
								radius={'md'}
							>
								<Title
									order={isMobile ? 4 : 2}
									fw={isMobile ? 'bold' : 'normal'}
									mb={10}
								>
									Store Statistics
								</Title>
								<Flex w={'100%'} align={'center'} justify={'space-between'}>
									<Tabs value={activeTab} onChange={setActiveTab}>
										<Tabs.List>
											<Tabs.Tab value='all' fz={'xs'}>
												Overall
											</Tabs.Tab>
											<Tabs.Tab value='thisMonth' fz={'xs'}>
												This month
											</Tabs.Tab>
											<Tabs.Tab value='thisWeek' fz={'xs'}>
												This week
											</Tabs.Tab>
											<Tabs.Tab value='thisYear' fz={'xs'}>
												This year
											</Tabs.Tab>
										</Tabs.List>

										<Tabs.Panel value='all' pt={20}>
											{loader.gettingBusinessProductCount ? (
												<Skeleton
													h={isMobile ? 40 : 35}
													w={'200px'}
													radius='md'
												/>
											) : (
												<Group>
													Products:{' '}
													<Text fw={600} fz={'lg'}>
														{businessProductCount?.data?.totalProducts}
													</Text>
												</Group>
											)}
										</Tabs.Panel>

										<Tabs.Panel value='thisMonth' pt={20}>
											{loader.gettingBusinessProductCount ? (
												<Skeleton
													h={isMobile ? 40 : 35}
													w={'200px'}
													radius='md'
												/>
											) : (
												<Group>
													Products:{' '}
													<Text fw={600} fz={'lg'}>
														{businessProductCount?.data?.totalProducts}
													</Text>
												</Group>
											)}
										</Tabs.Panel>

										<Tabs.Panel value='thisWeek' pt={20}>
											{loader.gettingBusinessProductCount ? (
												<Skeleton
													h={isMobile ? 40 : 35}
													w={'200px'}
													radius='md'
												/>
											) : (
												<Group>
													Products:{' '}
													<Text fw={600} fz={'lg'}>
														{businessProductCount?.data?.totalProducts}
													</Text>
												</Group>
											)}
										</Tabs.Panel>

										<Tabs.Panel value='thisYear' pt={20}>
											{loader.gettingBusinessProductCount ? (
												<Skeleton
													h={isMobile ? 40 : 35}
													w={'200px'}
													radius='md'
												/>
											) : (
												<Group>
													Products:{' '}
													<Text fw={600} fz={'lg'}>
														{businessProductCount?.data?.totalProducts}
													</Text>
												</Group>
											)}
										</Tabs.Panel>
									</Tabs>
								</Flex>
								<Divider my={15} size='xs' />
								{loader.gettingBusinessProductCount ? (
									<Flex w={'100%'} justify='space-between'>
										{[1, 2, 3].map((_, index) => (
											<Skeleton
												key={index}
												w={'30%'}
												h={isMobile ? 50 : 60}
												radius='md'
											/>
										))}
									</Flex>
								) : (
									<Flex w={'100%'} justify='space-between'>
										<StatusBox
											color='teal'
											bg='#CCF5E7'
											label='In Stock'
											count={businessProductCount?.data?.inStock}
										/>
										<StatusBox
											color='red'
											bg='#FFE0E0'
											label='Out of Stock'
											count={businessProductCount?.data?.outOfStock}
										/>
										<StatusBox
											color='gray'
											bg='#EAEFF6'
											label='Few Left'
											count={businessProductCount?.data?.fewLeft}
										/>
									</Flex>
								)}
							</Card>
						</Grid.Col>
					</Grid>
					<Grid>
						<Grid.Col span={{ base: 12, md: 12, lg: 12 }}>
							<Divider my={15} size='xs' />
							<Flex align={'center'} justify={'space-between'} mb={10}>
								<Title order={2} fw={'normal'}>
									Latest Orders
								</Title>
								<Text
									style={{
										cursor: 'pointer',
									}}
									c={Colors.primaryColor}
								>
									View All
								</Text>
							</Flex>
							<Flex
								w={'100%'}
								gap={'md'}
								direction={isMobile || isLaptop ? 'column' : 'row'}
							>
								<Flex w={isMobile || isLaptop ? '100%' : '60%'}>
									<ScrollArea w={'100%'} h={isMobile ? 200 : 450}>
										<Flex direction={'column'} gap='md' w={'100%'}>
											{loader.gettingOrdersByBusiness ? (
												<>
													{ordersByBusiness?.orders?.map(() => {
														return (
															<Skeleton
																h={60}
																w={'100%'}
																radius='md'
																key={Math.random()}
															/>
														)
													})}
												</>
											) : (
												<Fragment>
													{ordersByBusiness?.orders.map((order, index) => {
														return (
															<Flex
																align={'center'}
																justify={'space-between'}
																p='xs'
																w='100%'
																key={index}
																bg={'#62BE1212'}
																style={{
																	borderRadius: 12,
																	border: '1px solid #E5E7EB',
																}}
																pos={'relative'}
															>
																<Flex
																	w='100%'
																	align={'start'}
																	justify={'space-between'}
																>
																	<Flex w={isMobile ? '70%' : '60%'} gap={10}>
																		<Box h={40} w={40}>
																			<Image
																				src={order?.items[0]?.image}
																				h={40}
																				w={40}
																				style={{
																					borderRadius: 12,
																					objectFit: 'cover',
																				}}
																			/>
																		</Box>

																		<Flex
																			direction='column'
																			justify='center'
																			align='start'
																		>
																			<Text fw={500} size='md' lineClamp={1}>
																				{order?.items[0]?.title}
																			</Text>
																			<Text c='dimmed' size='xs'>
																				Price: ${' '}
																				{order?.totalOrderAmount?.amount.toFixed(
																					2
																				)}
																			</Text>
																		</Flex>
																	</Flex>
																	<Box>
																		<Badge
																			fw={500}
																			size='sm'
																			variant='light'
																			color={
																				order?.orderStatus === 'confirmed'
																					? 'green'
																					: 'red'
																			}
																			radius={'md'}
																			style={{
																				wordBreak: 'normal',
																				textWrap: 'nowrap',
																			}}
																		>
																			{order?.orderStatus}
																		</Badge>
																	</Box>
																</Flex>
															</Flex>
														)
													})}
												</Fragment>
											)}
										</Flex>
									</ScrollArea>
								</Flex>
								<Card
									w={isMobile || isLaptop ? '100%' : '40%'}
									p='md'
									radius='md'
									withBorder
									style={{
										borderColor: '#E5E7EB',
									}}
								>
									<Title order={3} ta={'center'} my={30} fw={400}>
										Monthly Overview
									</Title>
									<BarChart
										data={data}
										w={'100%'}
										h={200}
										dataKey='month'
										series={[
											{ name: 'Smartphones', color: 'violet.6' },
											{ name: 'Laptops', color: 'blue.6' },
											{ name: 'Tablets', color: 'teal.6' },
										]}
										tickLine='xy'
									/>
									<Flex my={10} align={'center'} justify={'center'} gap={20}>
										<Text size='xl' fw={'bold'}>
											30%
										</Text>
										<Text size='sm' c='dimmed'>
											Your sales performance is 30% better compare to last month
										</Text>
									</Flex>
									<Button
										size='md'
										bg={Colors.buttonColor}
										fw={400}
										c={'white'}
										mt={10}
									>
										See Orders
									</Button>
								</Card>
							</Flex>
							<Flex align={'center'} justify={'space-between'} mb={10}>
								<Title order={2} fw={'normal'} mt={15}>
									Recent Transactions
								</Title>
								<Text
									style={{
										cursor: 'pointer',
									}}
									c={Colors.primaryColor}
								>
									View All
								</Text>
							</Flex>
							<Table.ScrollContainer minWidth={500} h={450}>
								<Table
									horizontalSpacing='sm'
									verticalSpacing='md'
									striped
									highlightOnHover
									withTableBorder
									withColumnBorders
								>
									<Table.Thead>
										<Table.Tr bg={'#F5F6FA'}>
											<Table.Th>Transaction Name</Table.Th>
											<Table.Th>Amount</Table.Th>
											<Table.Th>Date</Table.Th>
											<Table.Th>Status</Table.Th>
										</Table.Tr>
									</Table.Thead>
									<Table.Tbody>{rows}</Table.Tbody>
								</Table>
							</Table.ScrollContainer>
						</Grid.Col>
					</Grid>
				</Grid.Col>
				<Grid.Col span={{ base: 12, md: 12, lg: 4 }}>
					<Grid>
						<Grid.Col span={{ base: 12, md: 12, lg: 12 }}>
							<Box
								bg='#f8f9fc'
								p='xs'
								style={{
									borderRadius: 12,
									border: '1px solid #E5E7EB',
								}}
								w={'100%'}
							>
								<Title order={3} mb='md'>
									Popular products
								</Title>
								<ScrollArea h={450}>
									<Stack gap='sm'>
										{loader.gettingTopRatedProducts ? (
											<>
												{topRatedProducts?.data?.map(() => {
													return (
														<Skeleton
															h={60}
															w={'100%'}
															radius='md'
															key={Math.random()}
														/>
													)
												})}
											</>
										) : (
											<>
												{topRatedProducts?.data?.length > 0 ? (
													topRatedProducts.data.map((product, index) => (
														<Group
															key={index}
															p='sm'
															bg='white'
															gap='sm'
															style={{ borderRadius: 12 }}
														>
															<Box h={60} w={60}>
																<Image
																	src={product?.productImage}
																	h='100%'
																	w='100%'
																	fallbackSrc={placeholder}
																	style={{ borderRadius: 12 }}
																/>
															</Box>
															<Box style={{ flex: 1 }}>
																<Text fw={400} lineClamp={2} fz={14}>
																	{product?.productName}
																</Text>
																<Text size='xs' c='gray'>
																	{product.category}
																</Text>
															</Box>
														</Group>
													))
												) : (
													<Flex
														w='100%'
														h={300}
														py='md'
														justify={'center'}
														align={'center'}
														style={{ textAlign: 'center' }}
													>
														<Text c='black' size='lg' fw={500}>
															No products found
														</Text>
													</Flex>
												)}
											</>
										)}
									</Stack>
								</ScrollArea>
							</Box>
						</Grid.Col>
						<Grid.Col span={{ base: 12, md: 12, lg: 12 }}>
							<Box
								bg='#f8f9fc'
								p='xs'
								style={{
									borderRadius: 12,
									border: '1px solid #E5E7EB',
								}}
								w={'100%'}
							>
								<Title order={3} mb='md'>
									Top Buyers
								</Title>
								<ScrollArea h={450}>
									<Stack gap='sm'>
										{loader.gettingTopBuyers ? (
											<>
												{topBuyers?.data?.map(() => {
													return (
														<Skeleton
															h={60}
															w={'100%'}
															radius='md'
															key={Math.random()}
														/>
													)
												})}
											</>
										) : (
											<>
												{topBuyers?.data?.length > 0 ? (
													topBuyers.data.map((buyer, index) => (
														<Group
															key={index}
															p='sm'
															bg='white'
															gap='sm'
															style={{ borderRadius: 12 }}
														>
															<Box h={60} w={60}>
																<Image
																	src={buyer?.user?.profilePic}
																	h='100%'
																	w='100%'
																	fallbackSrc={userPlaceholder}
																	style={{
																		borderRadius: 12,
																	}}
																/>
															</Box>
															<Box style={{ flex: 1 }}>
																<Text fw={400} lineClamp={2} fz={14}>
																	{buyer?.user?.firstName}
																</Text>
																<Text size='xs' c='gray'>
																	{buyer?.email}
																</Text>
															</Box>
														</Group>
													))
												) : (
													<Flex
														w='100%'
														h={300}
														py='md'
														justify={'center'}
														align={'center'}
														style={{ textAlign: 'center' }}
													>
														<Text c='black' size='lg' fw={500}>
															No buyers found
														</Text>
													</Flex>
												)}
											</>
										)}
									</Stack>
								</ScrollArea>
							</Box>
						</Grid.Col>
					</Grid>
				</Grid.Col>
			</Grid>
		</Flex>
	)
}

export default Dashboard
